import { useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MicIcon from '@mui/icons-material/Mic';

const FloatingWaveform = ({ isAISpeaking, isUserSpeaking, audioData }) => {
  const [position, setPosition] = useState({ x: window.innerWidth - 270, y: 20 });
  const [size, setSize] = useState({ width: 250, height: 150 }); // Rectangle dimensions
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const waveformRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const micStreamRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    setupMicrophone();
    initializeParticles();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (audioData && isAISpeaking) {
      playAudioAndVisualize(audioData);
    } else {
      visualizeMicrophone();
    }
  }, [isAISpeaking, isUserSpeaking, audioData, size]);

  const initializeParticles = () => {
    const particles = [];
    for (let i = 0; i < 100; i++) {
      particles.push({
        angle: Math.random() * Math.PI * 2,
        radius: Math.random() * 40 + 15,
        speed: Math.random() * 0.02 + 0.01,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.3
      });
    }
    particlesRef.current = particles;
  };

  const setupMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      microphone.connect(analyser);
      micAnalyserRef.current = analyser;
      
      visualizeMicrophone();
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const visualizeMicrophone = () => {
    if (!micAnalyserRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const analyser = micAnalyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (isAISpeaking) return;
      
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      const avgFrequency = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      const intensity = avgFrequency / 255;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const time = Date.now() * 0.001;

      // Draw glowing orb
      const baseRadius = isUserSpeaking ? 35 + intensity * 25 : 25;
      
      // Outer glow - BLUE for user speaking
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, baseRadius * 2
      );
      
      if (isUserSpeaking) {
        // Blue gradient when user speaks
        gradient.addColorStop(0, 'rgba(100, 200, 255, 0.9)');
        gradient.addColorStop(0.3, 'rgba(50, 150, 255, 0.7)');
        gradient.addColorStop(0.6, 'rgba(30, 100, 255, 0.4)');
        gradient.addColorStop(1, 'rgba(0, 50, 200, 0)');
      } else {
        // Grey when idle
        gradient.addColorStop(0, 'rgba(100, 100, 100, 0.3)');
        gradient.addColorStop(0.5, 'rgba(80, 80, 80, 0.2)');
        gradient.addColorStop(1, 'rgba(60, 60, 60, 0)');
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw rotating particles - BLUE for user
      particlesRef.current.forEach((particle, i) => {
        particle.angle += particle.speed * (isUserSpeaking ? 2 : 1);
        const x = centerX + Math.cos(particle.angle) * (particle.radius + intensity * 15);
        const y = centerY + Math.sin(particle.angle) * (particle.radius + intensity * 15);

        ctx.beginPath();
        ctx.arc(x, y, particle.size, 0, Math.PI * 2);
        
        if (isUserSpeaking) {
          // Blue particles
          ctx.fillStyle = `rgba(100, 200, 255, ${particle.opacity})`;
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(100, 200, 255, 0.8)';
        } else {
          ctx.fillStyle = `rgba(150, 150, 150, ${particle.opacity * 0.5})`;
          ctx.shadowBlur = 0;
        }
        ctx.fill();
      });

      ctx.shadowBlur = 0;

      // Pulsing rings - BLUE for user
      for (let i = 0; i < 3; i++) {
        const ringRadius = baseRadius + (i * 15) + Math.sin(time * 2 + i) * 5;
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = isUserSpeaking 
          ? `rgba(100, 200, 255, ${0.5 - i * 0.15})` 
          : `rgba(100, 100, 100, ${0.2 - i * 0.05})`;
        ctx.lineWidth = isUserSpeaking ? 2 : 1;
        ctx.stroke();
      }
    };

    draw();
  };

  const playAudioAndVisualize = async (base64Audio) => {
    try {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      const analyser = audioContext.createAnalyser();
      
      analyser.fftSize = 256;
      source.buffer = audioBuffer;
      source.connect(analyser);
      analyser.connect(audioContext.destination);
      source.start(0);

      visualizeAIAudio(analyser);
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const visualizeAIAudio = (analyser) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      const avgFrequency = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      const intensity = avgFrequency / 255;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const time = Date.now() * 0.001;
      const baseRadius = 35 + intensity * 30;

      // Rainbow gradient orb for AI
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, baseRadius * 2.5
      );
      
      const hue1 = (time * 50) % 360;
      const hue2 = (time * 50 + 120) % 360;
      const hue3 = (time * 50 + 240) % 360;
      
      gradient.addColorStop(0, `hsla(${hue1}, 100%, 70%, 0.9)`);
      gradient.addColorStop(0.3, `hsla(${hue2}, 100%, 60%, 0.6)`);
      gradient.addColorStop(0.6, `hsla(${hue3}, 100%, 50%, 0.3)`);
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Animated particles with rainbow colors for AI
      particlesRef.current.forEach((particle, i) => {
        particle.angle += particle.speed * 3;
        const x = centerX + Math.cos(particle.angle) * (particle.radius + intensity * 20);
        const y = centerY + Math.sin(particle.angle) * (particle.radius + intensity * 20);

        const hue = (i * 3.6 + time * 100) % 360;
        
        ctx.beginPath();
        ctx.arc(x, y, particle.size * (1 + intensity), 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue}, 100%, 60%, ${particle.opacity})`;
        ctx.fill();
        
        // Glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = `hsl(${hue}, 100%, 50%)`;
      });

      ctx.shadowBlur = 0;

      // Energy waves for AI - Rainbow
      for (let i = 0; i < 5; i++) {
        const waveRadius = baseRadius + (i * 12) + Math.sin(time * 3 + i) * 8;
        ctx.beginPath();
        ctx.arc(centerX, centerY, waveRadius, 0, Math.PI * 2);
        const waveHue = (time * 50 + i * 60) % 360;
        ctx.strokeStyle = `hsla(${waveHue}, 100%, 60%, ${0.5 - i * 0.08})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    };

    draw();
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle')) {
      setIsResizing(true);
    } else if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    } else if (isResizing) {
      const rect = waveformRef.current.getBoundingClientRect();
      setSize({
        width: Math.max(150, e.clientX - rect.left),
        height: Math.max(100, e.clientY - rect.top)
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, position]);

  return (
    <Paper
      ref={waveformRef}
      elevation={8}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: 9999,
        bgcolor: '#000000',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'default',
        transition: isDragging || isResizing ? 'none' : 'box-shadow 0.3s',
        border: isUserSpeaking 
          ? '2px solid rgba(100, 200, 255, 0.6)' 
          : isAISpeaking 
          ? '2px solid rgba(150, 100, 255, 0.4)' 
          : '2px solid rgba(60, 60, 60, 0.3)',
        '&:hover': {
          boxShadow: isUserSpeaking 
            ? '0 0 30px rgba(100, 200, 255, 0.5)'
            : isAISpeaking
            ? '0 0 30px rgba(150, 100, 255, 0.5)'
            : 6
        }
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Drag Handle */}
      <Box
        className="drag-handle"
        sx={{
          position: 'absolute',
          top: 5,
          left: 5,
          right: 5,
          height: 20,
          bgcolor: 'rgba(0,0,0,0.6)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          cursor: 'grab',
          zIndex: 10,
          border: '1px solid rgba(80, 80, 80, 0.3)',
          '&:active': {
            cursor: 'grabbing'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DragIndicatorIcon sx={{ fontSize: 12, color: 'grey.500' }} />
          <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.65rem' }}>
            AI Interviewer
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isAISpeaking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <VolumeUpIcon sx={{ fontSize: 12, color: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'success.main', fontSize: '0.6rem' }}>
                Speaking
              </Typography>
            </Box>
          )}
          {isUserSpeaking && !isAISpeaking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <MicIcon sx={{ fontSize: 12, color: '#64C8FF' }} />
              <Typography variant="caption" sx={{ color: '#64C8FF', fontSize: '0.6rem' }}>
                Listening
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Canvas - Centered circle */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        pt: 3
      }}>
        <canvas
          ref={canvasRef}
          width={Math.min(size.width, size.height) - 40}
          height={Math.min(size.width, size.height) - 40}
          style={{
            borderRadius: '50%'
          }}
        />
      </Box>

      {/* Resize Handle */}
      <Box
        className="resize-handle"
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: 20,
          height: 20,
          cursor: 'nwse-resize',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 12,
            height: 12,
            borderRight: '2px solid',
            borderBottom: '2px solid',
            borderColor: 'grey.600'
          }
        }}
      />
    </Paper>
  );
};

export default FloatingWaveform;